import { endOfYear, startOfYear, todayIso } from '@apka/core'
import { api } from './api.js'
import { KLUCZ_KURSORA, db, odczytajMeta, zapiszMeta } from './db.js'
import { nalozKolejke, ustawWyzwalaczSynchronizacji, wyslijKolejke, zZamkiem } from './kolejka.js'
import type { WynikWysylki } from './kolejka.js'
import type { Znaczniki } from './typy.js'
import type { KursWaluty, OdpowiedzSync } from './typy.js'

type Tabela = 'items' | 'payments' | 'categories' | 'vendors' | 'clients'

/**
 * Zapisuje partię rekordów: żywe trafiają do tabeli, usunięte miękko znikają
 * z kopii lokalnej. Bez tego drugiego kroku urządzenie, które było offline,
 * nigdy nie dowiedziałoby się o skasowaniu.
 */
async function zastosuj<T extends Znaczniki & { id: string }>(
  tabela: Tabela,
  rekordy: T[],
): Promise<void> {
  if (rekordy.length === 0) return
  const zywe = rekordy.filter((rekord) => rekord.deletedAt === null)
  const usuniete = rekordy.filter((rekord) => rekord.deletedAt !== null).map((rekord) => rekord.id)

  const cel = db[tabela] as unknown as {
    bulkPut(rows: T[]): Promise<unknown>
    bulkDelete(ids: string[]): Promise<unknown>
  }
  if (zywe.length > 0) await cel.bulkPut(zywe)
  if (usuniete.length > 0) await cel.bulkDelete(usuniete)
}

async function pobierzZmiany(): Promise<void> {
  const kursor = await odczytajMeta(KLUCZ_KURSORA)
  const sciezka =
    kursor === undefined ? '/api/sync' : `/api/sync?since=${encodeURIComponent(kursor)}`
  const dane = await api.get<OdpowiedzSync>(sciezka)

  await zastosuj('items', dane.items)
  await zastosuj('payments', dane.payments)
  await zastosuj('categories', dane.categories)
  await zastosuj('vendors', dane.vendors)
  await zastosuj('clients', dane.clients)
  await zapiszMeta(KLUCZ_KURSORA, dane.cursor)

  // Na wierzchu zostawiamy to, co czeka w kolejce — serwer jeszcze o tym nie wie,
  // a użytkownik ma prawo widzieć swoją zmianę bez migotania.
  await nalozKolejke()
}

/** Pobiera zmiany od ostatniego kursora. Nie wchodzi w drogę wysyłce kolejki. */
export function pull(): Promise<void> {
  return zZamkiem(pobierzZmiany)
}

/**
 * Kursy walut trzymamy od początku poprzedniego roku — tyle wystarcza, by
 * przeliczyć każdy termin widoczny w aplikacji, łącznie z notowaniem
 * zastępczym sprzed weekendu.
 */
export async function pobierzKursy(dzis = todayIso()): Promise<void> {
  const rok = Number(dzis.slice(0, 4))
  const od = startOfYear(rok - 1)
  const doDnia = endOfYear(rok + 1)
  const kursy = await api.get<KursWaluty[]>(`/api/fx?from=${od}&to=${doDnia}`)
  if (kursy.length > 0) await db.fxRates.bulkPut(kursy)
}

/**
 * Pełny obieg: najpierw oddajemy to, co czeka w kolejce, potem pobieramy
 * cudze zmiany. Odwrotna kolejność nadpisałaby lokalne zapisy stanem serwera,
 * który jeszcze o nich nie wie.
 */
export async function synchronizuj(): Promise<WynikWysylki> {
  const wynik = await wyslijKolejke()
  await pull()
  return wynik
}

// Każdy zapis lokalny natychmiast próbuje dojść do serwera.
ustawWyzwalaczSynchronizacji(() => {
  void synchronizuj().catch(() => undefined)
})

/** Pełne odświeżenie po zalogowaniu: dane domenowe plus kursy. */
export async function synchronizujWszystko(): Promise<void> {
  await synchronizuj()
  await pobierzKursy()
}

const ODBICIE_MS = 200
const MAX_PRZERWA_MS = 30_000
/** Zapasowy zegar na wypadek, gdy rozgłośnia milczy, a kolejka czeka. */
const PONOWNA_PROBA_MS = 60_000
/** Pierwsze ponowienie zaległej kolejki; kolejne wydłużają się dwukrotnie. */
const PIERWSZE_PONOWIENIE_MS = 1500

export interface OpcjeRealtime {
  przyBledzie?(blad: unknown): void
  przyWyniku?(wynik: WynikWysylki): void
}

/**
 * Utrzymuje połączenie z rozgłośnią i pilnuje kolejki. Powiadomienie nie
 * niesie danych — jest tylko sygnałem, że warto zsynchronizować. Kilka
 * powiadomień pod rząd łączymy w jedno pobranie.
 */
export function polaczRealtime(opcje: OpcjeRealtime = {}): () => void {
  let gniazdo: WebSocket | null = null
  let timerOdbicia: ReturnType<typeof setTimeout> | null = null
  let timerPonowienia: ReturnType<typeof setTimeout> | null = null
  let timerKolejki: ReturnType<typeof setTimeout> | null = null
  let przerwa = 1000
  let opoznienieKolejki = PIERWSZE_PONOWIENIE_MS
  let zamkniete = false

  /**
   * Zaległa kolejka nie może czekać na kolejne zdarzenie z zewnątrz: sieć
   * potrafi wrócić bez powiadomienia przeglądarki, a rozgłośnia bywa zerwana.
   * Dlatego po nieudanej wysyłce sami wracamy, coraz rzadziej.
   */
  const zaplanujPonowienie = () => {
    if (zamkniete) return
    if (timerKolejki !== null) clearTimeout(timerKolejki)
    timerKolejki = setTimeout(uruchom, opoznienieKolejki)
    opoznienieKolejki = Math.min(opoznienieKolejki * 2, MAX_PRZERWA_MS)
  }

  const uruchom = () => {
    synchronizuj().then(
      (wynik) => {
        if (wynik.zostalo > 0) zaplanujPonowienie()
        else opoznienieKolejki = PIERWSZE_PONOWIENIE_MS
        opcje.przyWyniku?.(wynik)
      },
      (blad: unknown) => {
        zaplanujPonowienie()
        opcje.przyBledzie?.(blad)
      },
    )
  }

  const zaplanuj = () => {
    if (timerOdbicia !== null) clearTimeout(timerOdbicia)
    timerOdbicia = setTimeout(uruchom, ODBICIE_MS)
  }

  const polacz = () => {
    if (zamkniete) return
    const protokol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    gniazdo = new WebSocket(`${protokol}://${window.location.host}/ws`)

    gniazdo.addEventListener('open', () => {
      przerwa = 1000
      zaplanuj()
    })

    gniazdo.addEventListener('message', (zdarzenie: MessageEvent<string>) => {
      try {
        const wiadomosc = JSON.parse(zdarzenie.data) as { type?: string }
        if (wiadomosc.type === 'changed') zaplanuj()
      } catch {
        // Nieznany format wiadomości ignorujemy — rozgłośnia nie niesie danych.
      }
    })

    gniazdo.addEventListener('close', () => {
      if (zamkniete) return
      timerPonowienia = setTimeout(polacz, przerwa)
      przerwa = Math.min(przerwa * 2, MAX_PRZERWA_MS)
    })
  }

  // Powrót sieci to najlepszy moment na opróżnienie kolejki.
  const poPowrocie = () => {
    opoznienieKolejki = PIERWSZE_PONOWIENIE_MS
    zaplanuj()
  }
  window.addEventListener('online', poPowrocie)
  const zegar = setInterval(() => {
    if (navigator.onLine) zaplanuj()
  }, PONOWNA_PROBA_MS)

  polacz()

  return () => {
    zamkniete = true
    window.removeEventListener('online', poPowrocie)
    clearInterval(zegar)
    if (timerOdbicia !== null) clearTimeout(timerOdbicia)
    if (timerPonowienia !== null) clearTimeout(timerPonowienia)
    if (timerKolejki !== null) clearTimeout(timerKolejki)
    gniazdo?.close()
  }
}
